import { Injectable } from '@angular/core';
import { DriverDto, PlacePredictionDto, PlacesApiService, VehicleDto } from '@fleet-beat/api';
import {
  AddressDto,
  CompanyDto,
  PriceDto,
  SelectOption,
  TemplateDirective,
  UserDto,
  addressToString,
} from '@fleet-beat/core';
import { DataAutocompleteFieldConfigV2 } from '@fleet-beat/editor';
import { AuthService } from '@fleet-beat/shared';
import { BehaviorSubject, Observable, map, of } from 'rxjs';
import { AddressAutocompleteStorageService } from './address-autocomplete-storage.service';
import { DriverListAutocompleteStorageService } from './driver-list-autocomplete-storage.service';
import { StatesAutocompleteStorageService } from './states-autocomplete-storage.service';
import { TimezoneAutocompleteStorageService } from './time-zone-autocomplete-storage.service';
import { TruckListAutocompleteStorageService } from './truck-list-autocomplete-storage.service';
import { CompanyListAdminAutocompleteStorageService } from './company-list-admin-autocomplete-storage.service';
import { TenantListAutocompleteStorageService } from './tenant-list-autocomplete-storage.service';
import { CompanyListAutocompleteStorageService } from './company-list-autocomplete-storage.service';
import { PriceListAdminAutocompleteStorageService } from './price-list-admin-autocomplete-storage.service';
import { PriceListAutocompleteStorageService } from './price-list-autocomplete-storage.service';

@Injectable({
  providedIn: 'root',
})
export class DataAutocompletePresetsService {
  get addressPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: PlacePredictionDto & AddressDto) => value.description || addressToString(value),
      transformSelected: (value: PlacePredictionDto) =>
        this.placesApiService.getOne(value.placeId, this.authService.authHeaders),
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find address',
      addButtonCaption: 'Use this address',
      storageParams: { offset: 0, limit: 5 },
      storage: this.addressAutocompleteStorageService.getPageableDataSource(),
      optionTemplate$: this.getTemplate('addressOptionTemplate'),
    };
  }

  get timeZonesPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: SelectOption) => value.value as string,
      transformSelected: (value: SelectOption) => of(value.value),
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find timezone',
      addButtonCaption: 'Use this timezone',
      storageParams: { offset: 0, limit: 5 },
      storage: this.timezoneAutocompleteStorageService.getPageableDataSource(),
      optionTemplate$: this.getTemplate('TimezoneOptionTemplate'),
    };
  }

  get driverListPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: DriverDto) => value.fullName,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find driver',
      addButtonCaption: 'Assingn this driver',
      storage: this.driverListAutocompleteStorageService.getPageableDataSource(),
      storageParams: { offset: 0, limit: 25, isActive: true, driverId: this.assignedDriverId },
      optionTemplate$: this.getTemplate('DriverListOptionTemplate'),
    };
  }

  get driverAndTrucksListPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: DriverDto) => value.fullName,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find driver',
      addButtonCaption: 'Assingn this driver',
      storage: this.driverListAutocompleteStorageService.getPageableDataSource(),
      storageParams: { offset: 0, limit: 25, isActive: true, include: 'truck', driverId: this.assignedDriverId },
      optionTemplate$: this.getTemplate('DriverAndTruckListOptionTemplate'),
    };
  }

  get companyListPresetAsAdmin(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: CompanyDto) => value.name,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find company',
      addButtonCaption: 'Assign this company',
      storage: this.companyListAdminAutocompleteStorageService.getPageableDataSource(),
      storageParams: { offset: 0, limit: 25 },
      optionTemplate$: this.getTemplate('CompanyOptionTemplate'),
    };
  }

  get pricesListPresetAsAdmin(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: PriceDto) => `$${value.unit_amount / 100}`,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find plan',
      addButtonCaption: 'Select this price',
      storage: this.priceListAdminAutocompleteStorageService.getPageableDataSource(),
      storageParams: { product: this.productId.getValue() },
      optionTemplate$: this.getTemplate('PriceListOptionTemplate'),
    };
  }

  get pricesListPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: PriceDto) => `$${value.unit_amount / 100}`,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find plan',
      addButtonCaption: 'Select this price',
      storage: this.priceListAutocompleteStorageService.getPageableDataSource(),
      storageParams: { product: this.productId.getValue() },
      optionTemplate$: this.getTemplate('PriceListOptionTemplate'),
    };
  }

  get companyListPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: CompanyDto) => value.name,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find company',
      addButtonCaption: 'Assign this company',
      storage: this.companyListAutocompleteStorageService.getPageableDataSource(),
      storageParams: { offset: 0, limit: 25 },
      optionTemplate$: this.getTemplate('CompanyOptionTemplate'),
    };
  }
  get tenantsListPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: CompanyDto) => value.name,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find service',
      addButtonCaption: 'Assign this service',
      storage: this.tenantListAutocompleteStorageService.getPageableDataSource(),
      storageParams: { offset: 0, limit: 25, isActive: true },
      optionTemplate$: this.getTemplate('TenantOptionTemplate'),
    };
  }
  get tenantsListPresetAll(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: CompanyDto) => value.name,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find service',
      addButtonCaption: 'Assign this service',
      storage: this.tenantListAutocompleteStorageService.getPageableDataSource(),
      storageParams: { offset: 0, limit: 25 },
      optionTemplate$: this.getTemplate('TenantOptionTemplate'),
    };
  }

  get stateListPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: SelectOption) => value.label as string,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find state',
      storage: this.statesAutocompleteStorageService.getPageableDataSource(),
      optionTemplate$: this.getTemplate('TimezoneOptionTemplate'),
    };
  }

  get userListPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: UserDto) => value.fullName,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find driver',
      addButtonCaption: 'Assingn this driver',
      storage: this.driverListAutocompleteStorageService.getPageableDataSource(),
      storageParams: { rsql: 'isDeleted=false AND role IN (2,3,4)', include: 'identity' },
      optionTemplate$: this.getTemplate('DriverListOptionTemplate'),
    };
  }

  get truckAndVinListPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: VehicleDto) => `${value.vehicleId}${value?.vin ? ' (' + value.vin + ')' : ''}`,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find truck',
      addButtonCaption: 'Assingn this truck',
      storage: this.truckListAutocompleteStorageService.getPageableDataSource(),
      storageParams: { offset: 0, limit: 25, isActive: true },
      optionTemplate$: this.getTemplate('TruckAndVinListOptionTemplate'),
    };
  }

  get truckListPreset(): Omit<DataAutocompleteFieldConfigV2, 'type' | 'name'> {
    return {
      valueToString: (value: VehicleDto) => value.vehicleId,
      createNew: (search: string) => ({ line1: search }),
      notFoundMessage: 'Type text to find truck',
      addButtonCaption: 'Assingn this truck',
      storage: this.truckListAutocompleteStorageService.getPageableDataSource(),
      storageParams: { offset: 0, limit: 25, isActive: true },
      optionTemplate$: this.getTemplate('TruckListOptionTemplate'),
    };
  }

  private templatesSubject: BehaviorSubject<TemplateDirective<object>[]> = new BehaviorSubject([]);

  private assignedDriverId: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private productId: BehaviorSubject<string> = new BehaviorSubject<string>('');

  constructor(
    private authService: AuthService,
    private addressAutocompleteStorageService: AddressAutocompleteStorageService,
    private driverListAutocompleteStorageService: DriverListAutocompleteStorageService,
    private companyListAdminAutocompleteStorageService: CompanyListAdminAutocompleteStorageService,
    private companyListAutocompleteStorageService: CompanyListAutocompleteStorageService,
    private tenantListAutocompleteStorageService: TenantListAutocompleteStorageService,
    private truckListAutocompleteStorageService: TruckListAutocompleteStorageService,
    private placesApiService: PlacesApiService,
    private timezoneAutocompleteStorageService: TimezoneAutocompleteStorageService,
    private statesAutocompleteStorageService: StatesAutocompleteStorageService,
    private priceListAdminAutocompleteStorageService: PriceListAdminAutocompleteStorageService,
    private priceListAutocompleteStorageService: PriceListAutocompleteStorageService,
  ) {
    this.setAssignedDriverId('');
    this.setProductId('');
  }

  setTemplates(templates: TemplateDirective<object>[]): void {
    this.templatesSubject.next(templates);
  }

  setAssignedDriverId(id: string): void {
    this.assignedDriverId.next(id);
  }

  setProductId(id: string): void {
    this.productId.next(id);
  }

  private getTemplate(name: string): Observable<TemplateDirective<object>> {
    return this.templatesSubject.pipe(
      map((templates: TemplateDirective<object>[]) =>
        templates.find((item: TemplateDirective<object>) => item.name === name),
      ),
    );
  }
}
